import { Controller, useForm } from "react-hook-form"
import { useVectorModal } from "../../../hooks/useVectorMemoryModal"
import { zodResolver } from "@hookform/resolvers/zod"
import z from "zod"

import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip
} from "@mui/material"
import { Warning } from "@phosphor-icons/react"

import type { VectorMetadataBulkTab } from "./vectorModal"

const newMetadataSchema = z.object({
	metadataKey: z.string().min(1, { message: 'Label key is required' }),
	newMetadataValue: z.string().min(1, { message: 'New label value is required' })
})

type NewMetadataValueData = z.infer<typeof newMetadataSchema>

interface VectorMetadataBulkChangeMetadataValueProps extends VectorMetadataBulkTab {}

function VectorMetadataBulkChangeMetadataValue({
	filesIds,
	metadatasData,
	selectedTab
}: VectorMetadataBulkChangeMetadataValueProps) {
	const {
		selectedStagingFiles,
		onMetadataValueChange,
	} = useVectorModal()

	const {
		register,
		handleSubmit,
		control,
		watch,
		formState: { errors },
		reset,
	} = useForm({
		defaultValues: {
			metadataKey: '',
			newMetadataValue: '',
		},
		resolver: zodResolver(newMetadataSchema)
	})

	const selectedMetadataName = watch('metadataKey')

	const metadataDataItem = metadatasData.find(({ name }) => name === selectedMetadataName)
	const isMetadataPresentInEveryFile = !!selectedMetadataName
		&& selectedStagingFiles.length === metadataDataItem?.presentOn.length

	const handleMetadataValueChange = ({
		metadataKey,
		newMetadataValue
	}: NewMetadataValueData) => {
		onMetadataValueChange({
			filesIds,
			metadatas: [{
				name: metadataKey,
				newValue: newMetadataValue
			}]
		})

		reset()
	}

	return (
		<Box
			role="tabpanel"
			hidden={selectedTab !== 'change-label-value'}
			id="tabpanel-bulk-metadata"
			aria-labelledby="tab-change-label-value"
			sx={{
				paddingInline: '2rem',
				'&>*+*': {
					marginBlockStart: '2rem !important'
				},
				'&[hidden]': {
					display: 'none',
				}
			}}
		>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(5, 1fr)',
					gap: '0.25rem',
				}}
			>
				<Box
					id="change-label-value-form"
					component="form"
					sx={{
						position: 'relative',
						gridColumn: 'span 1',
						display: 'flex',
						alignItems: 'center',
						gap: '0.25rem'
					}}
					onSubmit={handleSubmit(handleMetadataValueChange)}
				>
					<Controller
						control={control}
						name="metadataKey"
						render={({ field }) => (
							<>
								<FormControl 
									fullWidth
									variant="outlined"
									size="medium"
									error={!!errors.metadataKey}
								>
									<InputLabel 
										id="change-label-name-label"
										sx={{ color: "#9e9e9e" }}
									>
										{errors.metadataKey ? errors.metadataKey.message : "Pick a label key"}
									</InputLabel>

									<Select
										id="change-label-name-select"
										fullWidth
										sx={{
											marginBottom: 0,
										}}
										label="Pick a label key"
										labelId="change-label-name-label"
										value={field.value}
										onChange={(evt) => field.onChange(evt.target.value)}
									>
										{metadatasData.map(({ name }) => (
											<MenuItem key={name} value={name} sx={{ textTransform: 'capitalize' }}>
												{name.split('_').join(' ')}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								{!isMetadataPresentInEveryFile ? (
									<Tooltip 
										title="Because this label isn't present in every document, renaming it will add it to any documents that currently lack it."
										placement="top"
									>
										<Warning
											weight="fill"
											color="#ff9103"
											size={16}
										/>
									</Tooltip>
								) : null}
							</>
						)}
					/>
				</Box>

				<TextField 
					label={errors.newMetadataValue ? errors.newMetadataValue.message : "Replace label value"}
					sx={{ gridColumn: 'span 3' }}
					error={!!errors.newMetadataValue}
					{...register('newMetadataValue')}
				/>

				<Button
					type="submit"
					variant="outlined"
					form="change-label-value-form"
				>
						Apply
				</Button>
			</Box>
		</Box>
	)
}

export { VectorMetadataBulkChangeMetadataValue }